/**
 * Date helpers of the station view (#2675). Dates are ISO `YYYY-MM-DD`
 * strings. The arithmetic runs in UTC, so a daylight-saving change never moves
 * a date.
 */

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
// Fixed three-letter months: some ICU builds spell September "Sept".
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function parse(iso: string): Date {
  const [y = 0, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function format(d: Date): string {
  const y = String(d.getUTCFullYear()).padStart(4, "0");
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return format(d);
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000);
}

/** The Monday on or before `iso`. */
export function mondayOf(iso: string): string {
  const dow = (parse(iso).getUTCDay() + 6) % 7; // Mon = 0 … Sun = 6
  return addDays(iso, -dow);
}

/** The seven dates of the week that starts on `monday`. */
export function weekDates(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

export function weekdayShort(iso: string): string {
  return WEEKDAYS[parse(iso).getUTCDay()] ?? "";
}

/** "Mon 2 Nov". */
export function datePillLabel(iso: string): string {
  const d = parse(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

function clock(minute: number): string {
  const m = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export interface HoursSlot {
  date: string;
  label: string;
  start_minute: number;
  end_minute: number;
  end_day_offset?: number | undefined;
}

/** "Morning 08:00 to 13:00", or "Night 20:00 to 08:00, into Tue". */
export function hoursLabel(slot: HoursSlot): string {
  const base = `${slot.label} ${clock(slot.start_minute)} to ${clock(slot.end_minute)}`;
  const offset = slot.end_day_offset ?? 0;
  return offset > 0 ? `${base}, into ${weekdayShort(addDays(slot.date, offset))}` : base;
}

/** Today's date in an IANA zone. An unknown or missing zone reads the device zone. */
export function todayInZone(timeZone: string | undefined, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return format(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  }
}
